import { useEffect, useState } from "react";
import { Download, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, adminFileUrl, formatApiError, API } from "@/lib/api";

const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
  new: "bg-amber-100 text-amber-800",
  reviewed: "bg-sky-100 text-sky-800",
  contacted: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-200 text-slate-600",
};

const Badge = ({ value }) => (
  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLS[value] || "bg-slate-100 text-slate-600"}`}>
    {value || "—"}
  </span>
);

const inputCls = "field-input";

export function AdminApplications() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ search: "", status: "", country: "", specialization: "", degree: "", min_experience: "" });
  const [active, setActive] = useState(null);
  const [portalPw, setPortalPw] = useState("");
  const [rateVal, setRateVal] = useState("");
  const [researchRateVal, setResearchRateVal] = useState("");

  const load = () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ""));
    api.get("/admin/expert-applications", { params }).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filters]);

  const patch = async (id, payload) => {
    try {
      await api.patch(`/admin/expert-applications/${id}`, payload);
      toast.success("Updated");
      load();
      if (active?.id === id) setActive({ ...active, ...payload });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    await api.delete(`/admin/expert-applications/${id}`);
    toast.success("Deleted");
    setActive(null);
    load();
  };

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  return (
    <div data-testid="admin-applications">
      <div className="mb-5 grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-3 lg:grid-cols-6">
        <div className="relative sm:col-span-2">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="admin-app-search" className={`${inputCls} ps-9`} placeholder="Search by name..." value={filters.search} onChange={set("search")} />
        </div>
        <select data-testid="admin-app-filter-status" className={inputCls} value={filters.status} onChange={set("status")}>
          <option value="">Status: All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <input className={inputCls} placeholder="Country" value={filters.country} onChange={set("country")} data-testid="admin-app-filter-country" />
        <input className={inputCls} placeholder="Specialization" value={filters.specialization} onChange={set("specialization")} data-testid="admin-app-filter-specialization" />
        <input className={inputCls} placeholder="Degree" value={filters.degree} onChange={set("degree")} data-testid="admin-app-filter-degree" />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[760px] text-sm" data-testid="admin-applications-table">
          <thead>
            <tr className="border-b border-slate-100 text-start text-xs uppercase tracking-wider text-slate-500">
              {["Name", "Country", "Specialization", "Degree", "Exp.", "Status", "Directory", "Homepage", "Actions"].map((h) => (
                <th key={h} className="px-4 py-3 text-start font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="px-4 py-3 font-semibold text-navy">{a.full_name}</td>
                <td className="px-4 py-3 text-slate-600">{a.country}</td>
                <td className="px-4 py-3 text-slate-600">{a.specialization}</td>
                <td className="px-4 py-3 text-slate-600">{a.highest_degree}</td>
                <td className="px-4 py-3 text-slate-600">{a.years_experience}y</td>
                <td className="px-4 py-3"><Badge value={a.status} /></td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`app-publish-toggle-${a.id}`}
                    onClick={() => patch(a.id, a.published ? { published: false } : { status: "approved", published: true })}
                    className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${a.published ? "bg-gold text-navy" : "bg-slate-100 text-slate-500"}`}
                  >
                    {a.published ? "Published" : "Hidden"}
                  </button>
                  {a.published && <span className="mt-1 block text-[9px] text-slate-400">click to unpublish</span>}
                </td>
                <td className="px-4 py-3">
                  {a.status === "approved" && a.published ? (
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`app-feature-toggle-${a.id}`}
                        onClick={() => patch(a.id, { featured: !a.featured })}
                        title="Featured on Homepage"
                        className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${a.featured ? "bg-gold text-navy" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                      >
                        {a.featured ? "Featured" : "Feature"}
                      </button>
                      {a.featured && (
                        <input
                          type="number" min="0" defaultValue={a.featured_order ?? 0}
                          data-testid={`app-feature-order-${a.id}`}
                          onBlur={(e) => patch(a.id, { featured_order: parseInt(e.target.value || "0", 10) })}
                          title="Homepage display order (0 = first)"
                          className="w-14 rounded border border-slate-200 px-1.5 py-1 text-center text-xs"
                        />
                      )}
                    </div>
                  ) : (
                    <span className="text-[10px] text-slate-300" title="Only approved & published experts are eligible">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <button data-testid={`app-view-${a.id}`} onClick={() => { setActive(a); setRateVal(a.hourly_rate ?? 50); setResearchRateVal(a.research_hourly_rate ?? 50); setPortalPw(""); }} className="text-xs font-bold text-gold-hover hover:underline">View</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No applications found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {active && (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-navy-950/70 p-4" onClick={() => setActive(null)} data-testid="application-detail-modal">
          <div className="mx-auto my-8 max-w-3xl rounded-lg bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h2 className="font-display text-3xl font-semibold text-navy">{active.full_name}</h2>
                <p className="mt-1 text-sm text-slate-500">{active.job_title} {active.employer ? `· ${active.employer}` : ""}</p>
              </div>
              <button onClick={() => setActive(null)} className="rounded-full bg-slate-100 p-2" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
              {[
                ["Email", active.email], ["Phone", active.phone], ["WhatsApp", active.whatsapp],
                ["Country / City", [active.city, active.country].filter(Boolean).join(", ")],
                ["Degree", active.highest_degree], ["University", active.university],
                ["Specialization", active.specialization], ["Experience", `${active.years_experience} years`],
                ["Expertise", (active.expertise_areas || []).join(", ")], ["Skills", (active.key_skills || []).join(", ")],
                ["Languages", (active.languages || []).join(", ")], ["Regions", (active.work_regions || []).join(", ")],
                ["Online", active.online_availability], ["On-site", active.onsite_availability],
                ["LinkedIn", active.linkedin], ["Google Scholar", active.google_scholar], ["ORCID", active.orcid],
              ].map(([k, v]) => v ? (
                <div key={k}><span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">{k}</span><span className="break-words text-slate-700">{v}</span></div>
              ) : null)}
            </div>
            {[["Qualifications", active.qualifications], ["Training experience", active.training_experience],
              ["Consulting experience", active.consulting_experience], ["Sector experience", active.sector_experience],
              ["Biography", active.bio], ["Notes", active.notes]].map(([k, v]) => v ? (
              <div key={k} className="mt-4"><span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">{k}</span><p className="mt-1 whitespace-pre-line text-sm text-slate-700">{v}</p></div>
            ) : null)}
            <div className="mt-6 flex flex-wrap gap-3 border-t border-slate-100 pt-5">
              {active.cv_path && (
                <a href={adminFileUrl(active.cv_path)} data-testid="app-cv-download" className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white hover:bg-navy-800">
                  <Download className="h-3.5 w-3.5" /> {active.cv_filename || "Download CV"}
                </a>
              )}
              {active.photo_path && (
                <a href={adminFileUrl(active.photo_path)} data-testid="app-photo-download" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600">
                  <Download className="h-3.5 w-3.5" /> Photo
                </a>
              )}
            </div>
            <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-5" data-testid="portal-access-section">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Expert Portal Access &amp; Rate</h3>
              <p className="mt-2 text-xs text-slate-500">
                Portal account:{" "}
                {active.portal_active ? (
                  <span className="font-bold text-emerald-700" data-testid="portal-status-active">Active</span>
                ) : (
                  <span className="font-bold text-amber-700" data-testid="portal-status-inactive">Not created</span>
                )}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  data-testid="portal-invite-button"
                  onClick={async () => {
                    try {
                      await api.post(`/admin/expert-applications/${active.id}/portal-invite`, {});
                      toast.success(`Invitation emailed to ${active.email}`);
                    } catch (err) {
                      toast.error(formatApiError(err.response?.data?.detail));
                    }
                  }}
                  className="rounded-full bg-emerald-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-emerald-700"
                >
                  Send / Resend Invitation (resets password)
                </button>
                <span className="text-xs text-slate-400">Sent automatically on approval when the expert has an email</span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <input type="password" className={inputCls} style={{ maxWidth: 240 }} placeholder="New portal password (min 8)" value={portalPw} onChange={(e) => setPortalPw(e.target.value)} data-testid="portal-password-input" />
                <button
                  data-testid="portal-access-save-button"
                  onClick={async () => {
                    try {
                      await api.post(`/admin/expert-applications/${active.id}/portal-access`, { password: portalPw });
                      toast.success(`Portal login set for ${active.email}`);
                      setPortalPw("");
                    } catch (err) {
                      toast.error(formatApiError(err.response?.data?.detail));
                    }
                  }}
                  className="rounded-full bg-navy px-4 py-2.5 text-xs font-bold text-white hover:bg-navy-800"
                >
                  Set Portal Password
                </button>
                <span className="text-xs text-slate-400">Login email: {active.email || "none on file"}</span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <input type="number" min="1" className={inputCls} style={{ maxWidth: 180 }} value={rateVal} onChange={(e) => setRateVal(e.target.value)} data-testid="hourly-rate-input" />
                <button
                  data-testid="hourly-rate-save-button"
                  onClick={() => {
                    const v = parseFloat(rateVal);
                    if (!v || v <= 0) return toast.error("Enter a valid rate");
                    patch(active.id, { hourly_rate: v });
                  }}
                  className="rounded-full bg-gold px-4 py-2.5 text-xs font-bold text-navy hover:bg-gold-hover"
                >
                  Save Expert Rate (USD)
                </button>
                <input type="number" min="1" className={inputCls} style={{ maxWidth: 180 }} value={researchRateVal} onChange={(e) => setResearchRateVal(e.target.value)} data-testid="research-rate-input" />
                <button
                  data-testid="research-rate-save-button"
                  onClick={() => {
                    const v = parseFloat(researchRateVal);
                    if (!v || v <= 0) return toast.error("Enter a valid rate");
                    patch(active.id, { research_hourly_rate: v });
                  }}
                  className="rounded-full bg-gold px-4 py-2.5 text-xs font-bold text-navy hover:bg-gold-hover"
                >
                  Save Research Rate (USD)
                </button>
                <span className="text-xs text-slate-400">Type: {active.consultation_type || "expert"} · Default USD 50/hour</span>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 border-t border-slate-100 pt-5">
              {active.status !== "approved" && (
                <button data-testid="app-approve-button" onClick={() => patch(active.id, { status: "approved" })} className="rounded-full bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white hover:bg-emerald-700">Approve</button>
              )}
              {active.status !== "rejected" && (
                <button data-testid="app-reject-button" onClick={() => patch(active.id, { status: "rejected" })} className="rounded-full bg-red-100 px-5 py-2.5 text-xs font-bold text-red-700 hover:bg-red-200">Reject</button>
              )}
              {active.status === "approved" && (
                <button data-testid="app-publish-button" onClick={() => patch(active.id, { published: !active.published })} className="rounded-full bg-gold px-5 py-2.5 text-xs font-bold text-navy hover:bg-gold-hover">
                  {active.published ? "Unpublish" : "Publish to Directory"}
                </button>
              )}
              <button data-testid="app-delete-button" onClick={() => remove(active.id)} className="rounded-full border border-red-200 px-5 py-2.5 text-xs font-bold text-red-600 hover:bg-red-50">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PAYMENT_STATUSES = ["unpaid", "pending", "paid", "cancelled", "refunded"];
const REG_STATUSES = ["pending", "confirmed", "cancelled"];

export function AdminRegistrations() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");

  const load = () => api.get("/admin/registrations", { params: { search } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [search]);

  const patch = async (id, payload) => {
    await api.patch(`/admin/registrations/${id}`, payload);
    toast.success("Updated");
    load();
  };

  const exportCsv = () => {
    window.open(`${API}/admin/registrations-export?auth=${localStorage.getItem("stratedge-admin-token")}`, "_blank");
  };

  return (
    <div data-testid="admin-registrations">
      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="relative min-w-64 flex-1">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="admin-reg-search" className={`${inputCls} ps-9`} placeholder="Search name, email or course..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button onClick={exportCsv} data-testid="registrations-export-button" className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-bold text-white hover:bg-navy-800">
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[1100px] text-sm" data-testid="admin-registrations-table">
          <thead>
            <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
              {["Reg ID", "Name", "Email", "Course", "Amount due", "Status", "Payment", "Reference / Proof", "Date"].map((h) => (
                <th key={h} className="px-4 py-3 text-start font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="px-4 py-3 font-mono text-xs font-bold text-navy">{r.registration_id || "—"}</td>
                <td className="px-4 py-3 font-semibold text-navy">{r.full_name}</td>
                <td className="px-4 py-3 text-slate-600">{r.email}</td>
                <td className="px-4 py-3 text-slate-600">{r.course_title_en}</td>
                <td className="px-4 py-3 text-slate-700">
                  {r.amount_due != null ? (
                    <span>
                      {r.discounted_price != null && r.regular_price != null && (
                        <span className="me-1.5 text-xs text-slate-400 line-through">{r.currency} {Number(r.regular_price).toLocaleString()}</span>
                      )}
                      <span className="font-bold">{r.currency} {Number(r.amount_due).toLocaleString()}</span>
                    </span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3">
                  <select value={r.status} onChange={(e) => patch(r.id, { status: e.target.value })} data-testid={`reg-status-${r.id}`} className="rounded border border-slate-200 px-2 py-1 text-xs">
                    {REG_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select value={r.payment_status} onChange={(e) => patch(r.id, { payment_status: e.target.value })} data-testid={`reg-payment-${r.id}`} className="rounded border border-slate-200 px-2 py-1 text-xs">
                    {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {r.payment_reference && <span className="block font-mono">{r.payment_reference}</span>}
                  {r.payment_proof_path && (
                    <a href={adminFileUrl(r.payment_proof_path)} data-testid={`reg-proof-${r.id}`} className="font-bold text-gold-hover hover:underline">Proof</a>
                  )}
                  {!r.payment_reference && !r.payment_proof_path && "—"}
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No registrations yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const SUB_STATUSES = ["new", "reviewed", "contacted", "closed"];

export function AdminSubmissionsTable({ kind, title }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);

  const load = () => api.get(`/admin/submissions/${kind}`, { params: { search } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [kind, search]);

  const patch = async (id, status) => {
    await api.patch(`/admin/submissions/${kind}/${id}`, { status });
    toast.success("Updated");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`/admin/submissions/${kind}/${id}`);
    toast.success("Deleted");
    setActive(null);
    load();
  };

  return (
    <div data-testid={`admin-${kind}`}>
      <div className="mb-5 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="relative max-w-md">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input className={`${inputCls} ps-9`} placeholder={`Search ${title.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} data-testid={`admin-${kind}-search`} />
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
              {["Name", "Email", "Subject / Topic", "Status", "Date", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-start font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="px-4 py-3 font-semibold text-navy">{s.full_name}</td>
                <td className="px-4 py-3 text-slate-600">{s.email}</td>
                <td className="px-4 py-3 text-slate-600">{s.subject || s.topic || s.interest || "—"}</td>
                <td className="px-4 py-3">
                  <select value={s.status} onChange={(e) => patch(s.id, e.target.value)} data-testid={`submission-status-${s.id}`} className="rounded border border-slate-200 px-2 py-1 text-xs">
                    {SUB_STATUSES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{(s.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <button onClick={() => setActive(s)} data-testid={`submission-view-${s.id}`} className="text-xs font-bold text-gold-hover hover:underline">View</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No records yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {active && (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-navy-950/70 p-4" onClick={() => setActive(null)}>
          <div className="mx-auto my-8 max-w-2xl rounded-lg bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-5 flex items-start justify-between">
              <h2 className="font-display text-2xl font-semibold text-navy">{active.full_name}</h2>
              <button onClick={() => setActive(null)} className="rounded-full bg-slate-100 p-2" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
              {Object.entries(active)
                .filter(([k, v]) => v && !["id", "created_at", "status"].includes(k) && typeof v === "string")
                .map(([k, v]) => (
                  <div key={k}><span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">{k.replace(/_/g, " ")}</span><span className="break-words text-slate-700">{v}</span></div>
                ))}
            </div>
            {active.message && <p className="mt-4 whitespace-pre-line rounded-md bg-slate-50 p-4 text-sm text-slate-700">{active.message}</p>}
            {active.objectives && <p className="mt-4 whitespace-pre-line rounded-md bg-slate-50 p-4 text-sm text-slate-700">{active.objectives}</p>}
            {active.notes && <p className="mt-4 whitespace-pre-line rounded-md bg-slate-50 p-4 text-sm text-slate-700">{active.notes}</p>}
            <button onClick={() => remove(active.id)} className="mt-6 rounded-full border border-red-200 px-5 py-2.5 text-xs font-bold text-red-600 hover:bg-red-50">Delete</button>
          </div>
        </div>
      )}
    </div>
  );
}
